import { HttpNetworkError, type InternalActiveVisit, urlWithoutHash } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import * as Sentry from "@sentry/react";
import { t } from "@travily/i18n";
import { showToast } from "@ui/components/Toast";

export type NetworkErrorDisposition = "ignore" | "offline" | "report";

interface NetworkErrorContext {
  background: boolean;
  online: boolean;
  unloading: boolean;
  visibility: DocumentVisibilityState;
}

/**
 * How long a network toast stays on screen, and so the window in which a second failure must not
 * stack a duplicate on top of a visible one.
 */
const TOAST_DURATION_MS = 6_000;

/**
 * A page the user is walking away from tears down its in-flight XHRs, a backgrounded tab cannot
 * show a toast anyway, and prefetch or deferred-prop requests were fired by a hover heuristic or a
 * page mount rather than by a click — in all three cases "something went wrong" is noise about work
 * the user never asked for.
 */
export function classifyNetworkError({
  background,
  online,
  unloading,
  visibility,
}: NetworkErrorContext): NetworkErrorDisposition {
  if (background || unloading || visibility === "hidden") return "ignore";

  return online ? "report" : "offline";
}

function isBackgroundVisit(visit: InternalActiveVisit | undefined): boolean {
  return visit?.prefetch === true || visit?.deferredProps === true;
}

function trackPendingVisits() {
  let pending: InternalActiveVisit[] = [];

  return {
    // Inertia mutates `completed`/`cancelled`/`interrupted` on the very object it dispatched at
    // `start`, so settled visits can be pruned in place without a second listener.
    track(visit: InternalActiveVisit): void {
      pending = pending.filter((tracked) => !(tracked.completed || tracked.cancelled || tracked.interrupted));
      pending.push(visit);
    },

    findByUrl(url: string | undefined): InternalActiveVisit | undefined {
      if (!url) return undefined;

      return pending.filter((visit) => urlWithoutHash(visit.url).href === url).at(-1);
    },
  };
}

function throttleToasts(windowMs: number) {
  let lastShownAt = Number.NEGATIVE_INFINITY;

  return (show: () => void): void => {
    if (Date.now() - lastShownAt < windowMs) return;

    lastShownAt = Date.now();
    show();
  };
}

function reportNetworkError(error: Error, url: string | undefined): void {
  Sentry.captureException(error, {
    level: "warning",
    tags: { inertia_network_error: "true" },
    extra: { url },
  });
}

/**
 * Inertia's `Request.send()` re-rejects transport failures and `RequestStream.send()` never
 * catches, so without a listener every dropped XHR becomes an unhandled promise rejection and the
 * user gets a silently dead click.
 *
 * Registered from the entrypoint rather than a layout hook: the admin and preview layouts do not
 * mount PersistentLayout, and prefetch requests fire outside React's tree.
 */
export function registerNetworkErrorHandling(): void {
  // Chrome surfaces an XHR killed by document unload through `onerror`, not `onabort`, so
  // teardown is indistinguishable from a real transport failure without tracking it here.
  // `pagehide` also fires on entry to the bfcache, hence the reset when the page comes back.
  let unloading = false;
  window.addEventListener("pagehide", () => {
    unloading = true;
  });
  window.addEventListener("pageshow", () => {
    unloading = false;
  });

  const pendingVisits = trackPendingVisits();
  const toastOnce = throttleToasts(TOAST_DURATION_MS);

  // `networkError` carries the error alone, so whether a human is waiting on the response has to be
  // recovered from the `start` event that preceded it — the one event that fires for every request
  // and carries its visit. Inertia types that detail as a `PendingVisit` but dispatches the full
  // request params, which is where `deferredProps` lives.
  router.on("start", (event) => {
    pendingVisits.track(event.detail.visit as InternalActiveVisit);
  });

  router.on("networkError", (event) => {
    const { error } = event.detail;
    const failedUrl = error instanceof HttpNetworkError ? error.url : undefined;
    const visit = pendingVisits.findByUrl(failedUrl);

    const disposition = classifyNetworkError({
      background: isBackgroundVisit(visit),
      online: navigator.onLine,
      unloading,
      visibility: document.visibilityState,
    });

    if (disposition === "offline") {
      toastOnce(() =>
        showToast.warning(t("web.network_error.offline.title"), t("web.network_error.offline.description"), {
          duration: TOAST_DURATION_MS,
        }),
      );
    } else if (disposition === "report") {
      reportNetworkError(error, failedUrl);
      toastOnce(() =>
        showToast.error(t("web.network_error.title"), t("web.network_error.description"), {
          duration: TOAST_DURATION_MS,
          // Refetching the current page is the only safe retry: the event carries no HTTP method,
          // so replaying the failed visit could double-submit a POST.
          action: { label: t("web.network_error.retry"), onPress: () => router.reload() },
        }),
      );
    }

    // Returning `false` makes Inertia `preventDefault()` the event, which is what stops it
    // re-rejecting the failure. A prefetch must not be stopped: the cancel also skips
    // `onPrefetchError`, the only path that drops the request from Inertia's in-flight prefetch
    // registry, and a leaked entry makes every later click on that href await a promise that never
    // settles. Its rejection is absorbed below instead.
    return visit?.prefetch === true ? undefined : false;
  });

  // `preventDefault()` only suppresses the console report — Sentry instruments the
  // `window.onunhandledrejection` property, which the browser calls whatever a listener does, so
  // keeping the duplicate out of Sentry needs `beforeSend` (see entrypoints/instrument.tsx).
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason instanceof HttpNetworkError) event.preventDefault();
  });
}
